import Image from "next/image";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useMantineTheme } from "@mantine/core";
import classNames from "classnames";
import { FidelityBlockProps } from "@/interfaces/loyaltyProgram";
import {
  addBasePathToStrapiMedia,
  formatStrapiEntity,
  StrapiImage,
} from "@/utils/strapi";
import { injectPurifiedHTML } from "@/utils/injectPurifiedHTML";
import { removeEmptyRel } from "@/utils/removeEmptyRel";
import { Modal } from "@/components/common/modal/Modal";
import HydratedMediaQuery from "@/utils/hydratedMediaQuery";
import { getBreakpoint } from "@/utils/mantine";
import styles from "./FidelityBlock.module.scss";

const FidelityBlock = ({
  title,
  description,
  moreInformation,
  desktopBackgroundImage,
  mobileBackgroundImage,
  logo,
  titleLogo,
}: FidelityBlockProps) => {
  const intl = useIntl();
  const theme = useMantineTheme();

  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const strapiLogo: StrapiImage | null =
    logo.data !== null ? formatStrapiEntity(logo) : null;
  const strapiTitleLogo: StrapiImage | null =
    titleLogo.data !== null ? formatStrapiEntity(titleLogo) : null;
  const isTabletOrDesktop = HydratedMediaQuery(
    getBreakpoint(theme.breakpoints.sm)
  );
  const strapiDesktopBackgroundImage: StrapiImage | null =
    desktopBackgroundImage.data !== null
      ? formatStrapiEntity(desktopBackgroundImage)
      : null;
  const strapiMobileBackgroundImage: StrapiImage | null =
    mobileBackgroundImage.data !== null
      ? formatStrapiEntity(mobileBackgroundImage)
      : null;
  const strapiBackgroundImage: StrapiImage | null = isTabletOrDesktop
    ? strapiDesktopBackgroundImage
    : strapiMobileBackgroundImage;

  return (
    <div className={styles.global}>
      <div
        className={classNames(styles.container, {
          [styles.fidelityBlock]: strapiBackgroundImage == null,
          [styles.fidelityBlockWithBlueBackground]:
            strapiBackgroundImage !== null,
          [styles.clickableBlock]: moreInformation !== null,
        })}
        onClick={() => setIsModalOpened(true)}
      >
        {strapiBackgroundImage !== null ? (
          <div className={styles.backgroundImageContainer}>
            <Image
              src={addBasePathToStrapiMedia(strapiBackgroundImage)}
              objectFit="contain"
              objectPosition="center"
              alt={strapiBackgroundImage.alternativeText}
              layout="fill"
            />
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.informationWithLogo}>
              {strapiLogo !== null && (
                <div className={styles.logoContainer}>
                  <Image
                    src={addBasePathToStrapiMedia(strapiLogo)}
                    objectFit="contain"
                    objectPosition="center"
                    alt={strapiLogo.alternativeText}
                    layout="fill"
                  />
                </div>
              )}
              <div className={styles.information}>
                <div className={styles.informationLogoAndTitle}>
                  {strapiTitleLogo !== null && (
                    <div className={styles.titleLogoContainer}>
                      <Image
                        src={addBasePathToStrapiMedia(strapiTitleLogo)}
                        objectFit="contain"
                        objectPosition="center"
                        alt={strapiTitleLogo.alternativeText}
                        layout="fill"
                      />
                    </div>
                  )}
                  <div className={styles.title}>{title}</div>
                </div>
                {description !== null && (
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={injectPurifiedHTML(
                      removeEmptyRel(description)
                    )}
                  ></div>
                )}
              </div>
            </div>
            {moreInformation !== null && (
              <div className={styles.moreInformation}>
                <div className={styles.moreInformationLabel}>
                  {intl.formatMessage({ id: "moreInfos" })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isModalOpened && moreInformation !== null && (
        <Modal
          isOpen={isModalOpened}
          setIsOpen={setIsModalOpened}
          title={""}
          overrideStyles={{
            modal: styles.modal,
            title: styles.modalTitle,
          }}
        >
          <div
            className={styles.moreInformationModal}
            dangerouslySetInnerHTML={injectPurifiedHTML(
              removeEmptyRel(moreInformation)
            )}
          ></div>
        </Modal>
      )}
    </div>
  );
};

export default FidelityBlock;
